import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Seo } from '../../components/Seo';
import { MapPin, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const StateAuctioneers = () => {
  const { state } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/directory/state/${state}`)
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then((d) => setData(d))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [state]);

  if (notFound) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center text-gray-600" data-testid="state-not-found">
          No auctioneers found for this state.
        </div>
        <Footer />
      </>
    );
  }

  const name = data?.state_name || '';
  const total = data ? data.auctioneer_count + data.house_count : 0;

  const jsonLd = data && [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Find an Auctioneer', item: 'https://netauctionsinc.com/find-an-auctioneer' },
        { '@type': 'ListItem', position: 2, name, item: `https://netauctionsinc.com/find-an-auctioneer/${data.state_slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: `How many auctioneers are there in ${name}?`, acceptedAnswer: { '@type': 'Answer', text: `Net Auctions lists ${data.auctioneer_count.toLocaleString()} licensed auctioneers and ${data.house_count.toLocaleString()} auction companies across ${name}.` } },
        { '@type': 'Question', name: `How do I hire an auctioneer in ${name}?`, acceptedAnswer: { '@type': 'Answer', text: `Choose a city in ${name} below to see local auctioneers, then request a free quote or call them directly.` } },
      ],
    },
  ];

  return (
    <>
      <Navbar />
      {data && (
        <Seo
          title={`${name} Auctioneers — ${data.auctioneer_count.toLocaleString()} Licensed Auctioneers & Auction Companies | Net Auctions`}
          description={`Browse ${total.toLocaleString()} auctioneers and auction companies in ${name}. Find verified professionals for estate sales, real estate, farm equipment and business liquidations by city. Free directory.`}
          canonicalPath={`/find-an-auctioneer/${data.state_slug}`}
          jsonLd={jsonLd}
        />
      )}
      <div className="min-h-screen bg-gray-50" data-testid="state-page">
        <div className="bg-gradient-to-r from-green-700 to-green-800 text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-green-200 text-sm mb-3">
              <Link to="/find-an-auctioneer" className="hover:text-white">Find an Auctioneer</Link>
              <span className="mx-2">/</span>
              <span>{name}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3">Auctioneers in {name}</h1>
            {data && (
              <p className="text-lg text-green-100 max-w-3xl">
                {data.auctioneer_count.toLocaleString()} licensed auctioneers and {data.house_count.toLocaleString()} auction
                companies serving {name}. Select a city to view local professionals.
              </p>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading…</div>
          ) : (
            <>
              <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-6">
                Cities in {name}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="city-grid">
                {data.cities.map((c) => (
                  <Link
                    key={c.city_slug}
                    to={`/find-an-auctioneer/${data.state_slug}/${c.city_slug}`}
                    data-testid={`city-card-${c.city_slug}`}
                    className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-3 hover:border-green-500 hover:shadow-sm transition-all"
                  >
                    <span className="flex items-center gap-2 text-gray-800">
                      <MapPin className="w-4 h-4 text-green-600" /> {c.city}
                    </span>
                    <span className="flex items-center gap-2 text-sm text-gray-500">
                      {(c.auctioneer_count + c.house_count).toLocaleString()}
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};
